import React, { useState, useEffect } from 'react'

import { useFreeBuyBonusStore, useConfigurationStore, useBuyBonusStore, useFreeSpinsBonusesStore } from '@stores'

import { BSModalButton } from '@components'
import { useTranslation } from '@amusnet/translations/react'

import { BonusSystemState, BonusesPlayNowMessage, GameUIMessageType, BonusesPlayLaterMessage } from 'game-ui-types'

import { FreeBuyBonusBonus } from '@amusnet/mgs-ws-client'
interface FBBMainButtonsProps {
    bonus?: FreeBuyBonusBonus
}

const FBBMainButtons: React.FC<FBBMainButtonsProps> = ({ bonus }: FBBMainButtonsProps) => {
    const { t } = useTranslation()
    const bonusSystemState = useFreeBuyBonusStore((state) => state.bonusSystemState)
    const bonuses = useFreeBuyBonusStore((state) => state.bonuses)
    const setBonusSystemState = useFreeBuyBonusStore((state) => state.setBonusSystemState)
    const setHasActivatedBonus = useFreeBuyBonusStore((state) => state.setHasActivatedBonus)
    const setActiveBonus = useFreeBuyBonusStore((state) => state.setActiveBonus)

    const hasActiveBonusesLeft = useFreeSpinsBonusesStore((state) => state.hasActiveBonusesLeft) // logic is duplicated with fbb for the most par

    const buyBonusConfig = useConfigurationStore((state) => state.buyBonusConfig)

    const setMultiChoiceToggle = useBuyBonusStore((state) => state.setMultiChoiceToggle)
    const setSelectedFeatureIndex = useBuyBonusStore((state) => state.setSelectedFeatureIndex)
    const setSelectedFeature = useBuyBonusStore((state) => state.setSelectedFeature)

    const [primaryButtonText, setPrimaryButtonText] = useState('')
    const [secondaryButtonText, setSecondaryButtonText] = useState('')

    const isInSingleBonusState = bonusSystemState === BonusSystemState.SINGLE_BONUS_INITIAL
    const isInMultipleBonusesState = bonusSystemState === BonusSystemState.MULTIPLE_BONUSES_INITIAL
    const canPlayLater = isInSingleBonusState ? bonus?.playLater : bonuses.every((b) => b.playLater)

    useEffect(() => {
        switch (bonusSystemState) {
            case BonusSystemState.SINGLE_BONUS_INITIAL: {
                setPrimaryButtonText(t('gameui.playNow'))
                setSecondaryButtonText(t('gameui.playLater'))
                break
            }
            case BonusSystemState.MULTIPLE_BONUSES_INITIAL: {
                setPrimaryButtonText(t('gameui.playAllNow'))
                setSecondaryButtonText(t('gameui.playAllLater'))
                break
            }
        }
    }, [bonusSystemState])

    const handlePlayNowClick = () => {
        setHasActivatedBonus(true)

        const message: BonusesPlayNowMessage = {
            type: GameUIMessageType.BONUSES_PLAY_NOW,
            payload: { bonusIds: bonuses.map((b) => b.id) },
        }

        if (isInSingleBonusState) {
            if (bonus) {
                window.uiBus.postMessage(message)
            }

            setActiveBonus(bonus)
            setBonusSystemState(BonusSystemState.IN_PLAY)
        } else if (isInMultipleBonusesState) {
            window.uiBus.postMessage(message)

            const firstIndex = bonuses.findIndex((b) => b.isUnused)

            setActiveBonus(firstIndex === -1 ? undefined : bonuses[firstIndex])
            setBonusSystemState(BonusSystemState.IN_PLAY)
        }

        if (buyBonusConfig.shouldTheFeatureStayActive) {
            const selectedBonus = isInSingleBonusState ? bonus : bonuses.find((bonusItem) => bonusItem.isUnused)

            const freeBuyBonusConfig = selectedBonus?.typeConfiguration?.freeBuyBonusConfiguration
            const featureId = freeBuyBonusConfig?.featureId
            const guaranteedName = freeBuyBonusConfig?.guaranteedName

            const matchedFeatureIndex = buyBonusConfig.features.findIndex(
                (featureItem) => featureItem.name === featureId || featureItem.guaranteedName === guaranteedName
            )

            const matchedFeature = matchedFeatureIndex >= 0 ? buyBonusConfig.features[matchedFeatureIndex] : null

            setSelectedFeatureIndex(matchedFeatureIndex)
            setSelectedFeature(matchedFeature)

            if (!hasActiveBonusesLeft) {
                setMultiChoiceToggle(true)
            }
        }
    }

    const handlePlayLaterClick = () => {
        if (isInSingleBonusState && !bonus) {
            return
        }

        if (isInSingleBonusState || isInMultipleBonusesState) {
            const message: BonusesPlayLaterMessage = {
                type: GameUIMessageType.BONUSES_PLAY_LATER,
                payload: { bonusIds: bonuses.map((b) => b.id) },
            }

            window.uiBus.postMessage(message)
            setBonusSystemState(BonusSystemState.INACTIVE)
        }
    }

    return (
        <div className="flex justify-center gap-[20px] w-full px-[77px] py-[20px]">
            {canPlayLater && (
                <BSModalButton
                    id={secondaryButtonText.toLowerCase().replace(/\s+/g, '-')}
                    text={secondaryButtonText}
                    isPrimary={false}
                    onClick={handlePlayLaterClick}
                />
            )}
            <BSModalButton
                id={primaryButtonText.toLowerCase().replace(/\s+/g, '-')}
                text={primaryButtonText}
                onClick={handlePlayNowClick}
            />
        </div>
    )
}

export default FBBMainButtons
